import { api } from "@/lib/api-client"

export type SourceSettings = {
  include_extensions: string[]
  max_file_bytes: number
  interval_seconds: number | null
  acl_mode: string
}
export type ControlledSource = {
  uid: string
  name: string | null
  kind: string
  target: string | { site_id: string | null; site_path: string | null; drive_id: string | null }
  enabled: boolean
  revision: number
  settings: Omit<SourceSettings, "include_extensions" | "max_file_bytes"> & {
    include_extensions: string[] | null
    max_file_bytes: number | null
  }
  status: {
    state: string
    latest_job: {
      job_uid: string
      state: string
      created_at: number | null
      started_at: number | null
      finished_at: number | null
      phase?: string | null
      crawl_id?: number | null
      entities_done?: number | null
      entities_total?: number | null
    } | null
    error_code: string | null
    last_success_at?: number | string | null
  }
  bound_libraries: { id: string; name: string }[]
  effect?: string
}
export type SourceDescriptor = {
  api_version?: number
  service_profile?: string
  supported?: false
  capabilities: string[]
  source_kinds: string[]
  connector_apps: { name: string; kind: string }[]
  allowed_share_roots: string[]
  supported_acl_modes: string[]
  ceilings: { max_file_bytes: number; min_interval_seconds: number; site_path_prefixes: string[] }
}
export type SharePointSite = { site_id: string; name: string; web_url: string }
export type SharePointLibrary = { drive_id: string; name: string; web_url: string }
export type SourceInput = {
  kind: string
  name?: string
  app?: string
  site_id?: string
  drive_id?: string
  root_path?: string
  acl_mode?: string
  settings: Omit<SourceSettings, "acl_mode">
}
const base = (id: string) => `/admin/filth-backends/${encodeURIComponent(id)}`
const result = <T>(r: { data?: unknown }) => (r.data || r) as T
export async function getSourceControl(id: string) {
  return result<SourceDescriptor>(await api.get(`${base(id)}/control`))
}
export async function getControlledSource(id: string, uid: string) {
  return result<ControlledSource>(await api.get(`${base(id)}/sources/${encodeURIComponent(uid)}`))
}
export async function findSourceSites(id: string, app: string, q: string) {
  return result<{ sites: SharePointSite[]; mode: "search" | "resolve"; truncated?: boolean }>(
    await api.get(`${base(id)}/sharepoint/sites`, { params: { app, ...(q ? { q } : {}) } })
  )
}
export async function getSourceLibraries(id: string, app: string, site: string) {
  return result<{ libraries: SharePointLibrary[]; truncated?: boolean }>(
    await api.get(`${base(id)}/sharepoint/sites/${encodeURIComponent(site)}/libraries`, {
      params: { app },
    })
  )
}
export async function browseSourceShare(id: string, path: string) {
  return result<{ directories: { path: string; name: string }[] }>(
    await api.get(`${base(id)}/filesystem/browse`, { params: { path } })
  )
}
type SourceAction = { body: unknown; key: string; source?: ControlledSource }
export function changeSource(
  id: string,
  operation: "sync",
  action: SourceAction
): Promise<{ job_uid: string; deduplicated: boolean }>
export function changeSource(
  id: string,
  operation: "delete",
  action: SourceAction
): Promise<{ uid: string; retirement_state: "retired" }>
export function changeSource(
  id: string,
  operation: "create" | "update" | "pause" | "resume",
  action: SourceAction
): Promise<ControlledSource>
export function changeSource(
  id: string,
  operation: "sync" | "pause" | "resume",
  action: SourceAction
): Promise<ControlledSource | { job_uid: string; deduplicated: boolean }>
export async function changeSource(
  id: string,
  operation: "create" | "update" | "sync" | "pause" | "resume" | "delete",
  action: { body: unknown; key: string; source?: ControlledSource }
) {
  const { body, key, source } = action
  const url = `${base(id)}/sources${source ? `/${encodeURIComponent(source.uid)}` : ""}`
  const headers = {
    "Idempotency-Key": key,
    ...(source && (operation === "update" || operation === "delete")
      ? { "If-Match": `"${source.revision}"` }
      : {}),
  }
  if (operation === "update")
    return result<ControlledSource>(await api.patch(url, body, { headers }))
  if (operation === "delete")
    return result<{ uid: string; retirement_state: "retired" }>(
      await api.delete(url, { headers, data: body })
    )
  if (operation === "sync")
    return result<{ job_uid: string; deduplicated: boolean }>(
      await api.post(`${url}/sync`, body, { headers })
    )
  return result<ControlledSource>(
    await api.post(operation === "create" ? url : `${url}/${operation}`, body, { headers })
  )
}
